// =============================================================================
// Hosted-agent eval suite — run-level state machines (#3028)
//
// Three things that must not be decided ad hoc inside a component, because
// each one has a way of failing silently:
//
//   1. WHEN TO STOP POLLING. POST /eval-runs answers 202 and the run document
//      is seeded into the cache as `running`. If the follow-up GET then 404s
//      or the network drops, the seeded document still says `running` for
//      ever — so the poll plan looks at read failures and wall-clock age, not
//      only at `status`.
//   2. HOW LONG A 429 REALLY LASTS. The backend has two different bounds (a
//      ~60 s cooldown and a 12-per-day cap), and the daily one returns up to
//      86 400 seconds. "Try again in 84512 s" is not an answer to a human.
//   3. WHETHER TWO RUNS MAY BE COMPARED AT ALL. The refusal is the feature;
//      the reason is rendered as visible text, so it is computed here where it
//      can be tested rather than hidden in a tooltip.
// =============================================================================

import type { HostedAgentEvalRun } from "@/api/admin-api"

import {
  canCompareHostedAgentEvalRun,
  hostedAgentEvalComparability,
  hostedAgentEvalSameExam,
  summarizeHostedAgentEvalTotals,
} from "./hosted-agent-evals"

// ---------------------------------------------------------------------------
// Poll plan
// ---------------------------------------------------------------------------

/** Base cadence while a suite is fresh — the repo's canonical 3 s. */
export const HOSTED_AGENT_EVAL_POLL_MS = 3000
/**
 * Consecutive failed reads after which polling gives up and says so. React
 * Query counts every attempt, retries included (the run query retries once),
 * so this is roughly three failed polls — about ten seconds of silence.
 */
export const HOSTED_AGENT_EVAL_POLL_FAILURE_LIMIT = 6
/** After this the run document is treated as stuck, not as in progress. */
export const HOSTED_AGENT_EVAL_POLL_MAX_MS = 2 * 60 * 60 * 1000

const POLL_BACKOFF_STEPS: { afterMs: number; intervalMs: number }[] = [
  { afterMs: 10 * 60 * 1000, intervalMs: 10_000 },
  { afterMs: 60 * 60 * 1000, intervalMs: 30_000 },
]

export type HostedAgentEvalPollPlan = {
  /**
   * `settled` — terminal (or nothing to poll).
   * `polling` — keep reading at `intervalMs`.
   * `unreadable` — the run exists but cannot be read; polling stopped.
   * `stalled` — still `running` long past any plausible suite; polling stopped.
   */
  state: "settled" | "polling" | "unreadable" | "stalled"
  intervalMs: number | false
}

/**
 * Decide whether to keep polling one run. `failureCount` is React Query's
 * consecutive-failure count for the query (it resets on every success), which
 * is the only signal that distinguishes "still running" from "we have not been
 * able to read it since it started".
 */
export function planHostedAgentEvalPoll(
  run: HostedAgentEvalRun | undefined,
  failureCount = 0,
  now: number = Date.now()
): HostedAgentEvalPollPlan {
  if (!run || run.status !== "running") return { state: "settled", intervalMs: false }
  if (failureCount >= HOSTED_AGENT_EVAL_POLL_FAILURE_LIMIT) {
    return { state: "unreadable", intervalMs: false }
  }
  const startedAt = Date.parse(run.started_at)
  const elapsed = Number.isFinite(startedAt) ? Math.max(0, now - startedAt) : 0
  if (elapsed >= HOSTED_AGENT_EVAL_POLL_MAX_MS) return { state: "stalled", intervalMs: false }
  const step = [...POLL_BACKOFF_STEPS].reverse().find((candidate) => elapsed >= candidate.afterMs)
  return { state: "polling", intervalMs: step?.intervalMs ?? HOSTED_AGENT_EVAL_POLL_MS }
}

// ---------------------------------------------------------------------------
// 429 — two different bounds, two different answers
// ---------------------------------------------------------------------------

/** Mirrors `EVAL_SUITE_COOLDOWN_SECONDS` in `services/hosted_agents/eval_runner.py`. */
export const HOSTED_AGENT_EVAL_COOLDOWN_SECONDS = 60
/** Mirrors `MAX_EVAL_SUITE_RUNS_PER_DAY`. */
export const HOSTED_AGENT_EVAL_RUNS_PER_DAY = 12

export type HostedAgentEvalRetryPlan = {
  /** Which bound the server hit — the two need different copy. */
  limit: "cooldown" | "daily"
  /** The unit the remaining wait is worth stating in. */
  unit: "seconds" | "minutes" | "hours"
  /** The remaining wait in `unit`, never below 1 while time is left. */
  value: number
  /** How often the countdown should tick — never once a second for a day. */
  tickMs: number
  /** Seconds the countdown should subtract per tick. */
  stepSeconds: number
}

/**
 * Turn `retry_after_seconds` into something readable. A cooldown counts down in
 * seconds because the admin is about to click again; a daily cap is stated in
 * minutes or hours because the honest answer is "come back later".
 */
export function planHostedAgentEvalRetry(seconds: number): HostedAgentEvalRetryPlan {
  const remaining = Number.isFinite(seconds) ? Math.max(0, Math.ceil(seconds)) : 0
  const limit = remaining <= HOSTED_AGENT_EVAL_COOLDOWN_SECONDS ? "cooldown" : "daily"
  if (remaining < 90) {
    return { limit, unit: "seconds", value: remaining, tickMs: 1000, stepSeconds: 1 }
  }
  if (remaining < 90 * 60) {
    return {
      limit,
      unit: "minutes",
      value: Math.max(1, Math.ceil(remaining / 60)),
      tickMs: 30_000,
      stepSeconds: 30,
    }
  }
  return {
    limit,
    unit: "hours",
    value: Math.max(1, Math.round(remaining / 3600)),
    tickMs: 60_000,
    stepSeconds: 60,
  }
}

// ---------------------------------------------------------------------------
// Comparability between two runs
// ---------------------------------------------------------------------------

export type HostedAgentEvalRunDelta = {
  passed: number
  failed: number
  unmeasured: number
}

export type HostedAgentEvalCompareState =
  | { kind: "comparable"; delta: HostedAgentEvalRunDelta }
  | {
      kind:
        | "unfinished"
        | "partial"
        | "notComparable"
        | "noPrevious"
        | "differentTest"
        | "noBaseline"
    }

/**
 * Whether this run may be read against the previous one — and if not, why.
 * The questions are asked in the order an admin would ask them; the first one
 * that refuses wins.
 */
export function hostedAgentEvalCompareState(
  run: HostedAgentEvalRun,
  previous: HostedAgentEvalRun | undefined
): HostedAgentEvalCompareState {
  if (run.status !== "completed") return { kind: "unfinished" }
  if (run.partial) return { kind: "partial" }
  if (hostedAgentEvalComparability(run).notComparable) return { kind: "notComparable" }
  if (!previous) return { kind: "noPrevious" }
  if (!hostedAgentEvalSameExam(run, previous)) return { kind: "differentTest" }
  if (!canCompareHostedAgentEvalRun(previous)) return { kind: "noBaseline" }

  const current = summarizeHostedAgentEvalTotals(run.totals, run.status)
  const baseline = summarizeHostedAgentEvalTotals(previous.totals, previous.status)
  return {
    kind: "comparable",
    delta: {
      passed: current.passed - baseline.passed,
      failed: current.failed - baseline.failed,
      unmeasured: current.unmeasured - baseline.unmeasured,
    },
  }
}

/** `+2` / `-1` / `±0` — a delta has to carry its sign to be readable. */
export function formatHostedAgentEvalDelta(value: number): string {
  if (value > 0) return `+${value}`
  if (value < 0) return `${value}`
  return "±0"
}

// ---------------------------------------------------------------------------
// History row status
// ---------------------------------------------------------------------------

/**
 * The mark that heads a history row. Four distinct readings, never folded:
 * a suite still `running` is NOT the same as one that finished without being
 * measurable, and a crashed suite is an error in our machinery rather than an
 * agent failure. "passed" is reserved for a finished run with nothing failed
 * and nothing unmeasured.
 */
export function hostedAgentEvalRunRowStatus(run: HostedAgentEvalRun): string {
  if (run.status === "running") return "running"
  if (run.status === "failed") return "errored"
  const view = summarizeHostedAgentEvalTotals(run.totals, run.status)
  if (view.failed > 0) return "failed"
  if (view.unmeasured > 0) return "indeterminate"
  return "passed"
}
